import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import styled from 'styled-components';

import { createShopRequests } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import Modal from 'components/lib/Modal';
import { getDateString } from 'helpers/dates';

import CreateShopRequestModalContent from './CreateShopRequestModalContent';
import { ShopRequestModalContext } from './ShopRequestModalContext';
import {
  END_REPEAT_VALUES,
  MYSTERY_SHOP_MODAL_REQUEST_FROM,
  MYSTERY_SHOP_REQUEST_DUE_DATES,
  SHOP_REQUEST_REPEAT_OCCURENCES,
  SHOP_REQUEST_STATUS,
  SHOP_REQUEST_TYPE,
} from '../constants';
import { useShopRequestsContext } from '../ShopRequestsContext';
import { useShopRequestsDataContext } from '../ShopRequestsDataContext';
import { MysteryShopRequestGroup, ShopRequestCreationObject, ShopRequestType } from '../types';
import { createShopRequestOutbound, getBaseCommunityShopRequest, getShopRequestName } from '../utils';

const StyledModal = styled(Modal)`
  .ant-modal-close {
    top: 22px;
  }

  &&& .ant-modal-body {
    display: flex;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &&& .ant-modal-footer {
    margin-top: 0;
    button {
      font-size: 14px;
    }
  }

  .ant-modal-content {
    height: 86vh;
    display: flex;
    flex-flow: column;

    .ant-modal-header {
      margin-bottom: 0;
    }
    .ant-modal-footer {
      display: flex;
      flex-flow: row-reverse;

      button {
        margin-right: 10px;
        border-radius: 4px;
        width: 155px;
      }
    }
  }
`;

type Props = {
  open: boolean;
  onCancel: () => void;
};

const createRequestTypeChangeDraft = ({
  shopRequest,
  requestType,
  shopperId,
  auditorId,
}: {
  shopRequest: ShopRequestCreationObject;
  requestType: ShopRequestType;
  shopperId: number | null;
  auditorId: number | null;
}): ShopRequestCreationObject => ({
  communityId: shopRequest.communityId,
  communityName: shopRequest.communityName,
  dueDate: shopRequest.dueDate,
  shopperId,
  auditorId,
  requestType,
  independentLiving: false,
  assistedLiving: false,
  memoryCare: false,
  adminNote: null,
  status: SHOP_REQUEST_STATUS[0].id,
  propagation: {
    propagate_self: false,
    propagate_others: false,
  },
});

const CreateShopRequestModal = ({ open, onCancel }: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const { staffUsers } = useShopRequestsContext();
  const { filters, ordering, setFilters } = useShopRequestsDataContext();

  const [isLoading, setLoading] = useState(false);
  const [requestFrom, setRequestFrom] = useState(MYSTERY_SHOP_MODAL_REQUEST_FROM.COMPANY);
  const [requestType, setRequestType] = useState<ShopRequestType>('mystery_shop');
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [shopRequests, setShopRequests] = useState<ShopRequestCreationObject[]>([]);
  const [dueDate, setDueDate] = useState<string>(MYSTERY_SHOP_REQUEST_DUE_DATES.IN_3_DAYS);
  const [repeat, setRepeat] = useState<number>(SHOP_REQUEST_REPEAT_OCCURENCES.EVERY_MONTH);
  const [repeatEnabled, setRepeatEnabled] = useState<boolean>(false);
  const [endRepeat, setEndRepeat] = useState(END_REPEAT_VALUES.AFTER_1_YEAR);
  const [specificDueDate, setSpecificDueDate] = useState(new Date());
  const [shopperId, setShopperId] = useState<number | null>(null);
  const [auditorId, setAuditorId] = useState<number | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [shopForBaseCommunity, setShopForBaseCommunity] = useState(false);
  const [selectedBaseCommunity, setSelectedBaseCommunity] = useState<any>(null);
  // TODO: use Antd Form instead in CreateShopRequestModal instead of manually handling the state
  const [baseCommunityIL, setBaseCommunityIL] = useState(false);
  const [baseCommunityAL, setBaseCommunityAL] = useState(false);
  const [baseCommunityMC, setBaseCommunityMC] = useState(false);
  const [baseCommunityPropagateSelf, setBaseCommunityPropagateSelf] = useState(false);
  const [baseCommunityPropagateOthers, setBaseCommunityPropagateOthers] = useState(false);
  const isPricingRequest = requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP;
  const shouldCreateBaseCommunityShopRequest = !isPricingRequest || shopForBaseCommunity;

  const isOkButtonDisabled =
    isLoading || !selectedBaseCommunity?.id || (!shopRequests.length && !shouldCreateBaseCommunityShopRequest);

  useEffect(() => setShopForBaseCommunity(false), [selectedCompany]);

  const handleConfirm = async () => {
    try {
      if (shouldCreateBaseCommunityShopRequest && !selectedBaseCommunity?.id) {
        showMessage('error', 'Select a base community before creating a base community shop request.');
        return;
      }

      setLoading(true);
      const requests = shopRequests.map((r) => ({
        ...r,
        independentLiving: requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? r.independentLiving : false,
        assistedLiving: requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? r.assistedLiving : false,
        memoryCare: requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? r.memoryCare : false,
        shopperId,
        auditorId,
        dueDate: getDateString(dueDate, specificDueDate),
        repeat,
        requestFrom: requestFrom === MYSTERY_SHOP_MODAL_REQUEST_FROM.COMPANY ? selectedCompany : null,
        status: SHOP_REQUEST_STATUS[0].id,
        requestType,
        propagation: isPricingRequest
          ? r.propagation
          : {
              propagate_self: false,
              propagate_others: false,
            },
      }));
      if (shouldCreateBaseCommunityShopRequest) {
        const baseCommunityShopRequest = getBaseCommunityShopRequest({
          baseCommunityId: selectedBaseCommunity?.id,
          independentLiving: requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? baseCommunityIL : false,
          assistedLiving: requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? baseCommunityAL : false,
          memoryCare: requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? baseCommunityMC : false,
          dueDate,
          specificDueDate,
          repeat,
          shopperId,
          auditorId,
          requestFrom,
          selectedCompany,
          propagation: {
            propagate_self: isPricingRequest && baseCommunityPropagateSelf,
            propagate_others: isPricingRequest && baseCommunityPropagateOthers,
          },
          requestType,
        });
        requests.push(baseCommunityShopRequest);
      }

      const payload: MysteryShopRequestGroup = createShopRequestOutbound(
        getShopRequestName(name, requestFrom, selectedBaseCommunity),
        selectedBaseCommunity?.id,
        selectedCompany || undefined,
        repeatEnabled ? repeat : 0,
        repeatEnabled ? endRepeat : 0,
        requests
      );
      await createShopRequests(payload).then(() => {
        setFilters({ ...filters, page: 1 });
        queryClient.invalidateQueries(['shopRequests', filters, ordering]);
        queryClient.invalidateQueries(['shop-requests-tabs-metrics', filters]);
        onCancel();
        showMessage('success', 'Shop requests created');
        setLoading(false);
      });
    } catch (e: any) {
      setLoading(false);
      showMessage('error', `Trouble creating shop requests: ${e?.response?.data}`);
      console.error('Trouble creating shop requests.', e);
    }
  };

  const selectCommunity = useCallback(
    (community: any) => {
      if (!community?.id) {
        return;
      }

      setShopRequests([
        ...shopRequests,
        {
          communityId: community.id,
          communityName: community.comunity_name,
          independentLiving: true,
          assistedLiving: true,
          memoryCare: true,
          adminNote: null,
          dueDate: getDateString(dueDate),
          repeat,
          shopperId,
          auditorId,
          requestType,
          propagation: {
            propagate_self: false,
            propagate_others: requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP,
          },
        },
      ]);
    },
    [setShopRequests, shopRequests, requestType]
  );

  const handleRequestTypeChange = useCallback(
    (nextRequestType: ShopRequestType) => {
      if (nextRequestType === requestType) {
        return;
      }

      setRequestType(nextRequestType);
      setShopRequests(
        shopRequests.map((shopRequest) =>
          createRequestTypeChangeDraft({
            shopRequest,
            requestType: nextRequestType,
            shopperId,
            auditorId,
          })
        )
      );
      setRepeat(SHOP_REQUEST_REPEAT_OCCURENCES.EVERY_MONTH);
      setRepeatEnabled(false);
      setEndRepeat(END_REPEAT_VALUES.AFTER_1_YEAR);
      setShopForBaseCommunity(false);
      setBaseCommunityIL(false);
      setBaseCommunityAL(false);
      setBaseCommunityMC(false);
      setBaseCommunityPropagateSelf(false);
      setBaseCommunityPropagateOthers(false);
    },
    [
      auditorId,
      requestType,
      shopRequests,
      shopperId,
      setBaseCommunityAL,
      setBaseCommunityIL,
      setBaseCommunityMC,
      setBaseCommunityPropagateOthers,
      setBaseCommunityPropagateSelf,
      setEndRepeat,
      setName,
      setRepeat,
      setRepeatEnabled,
      setRequestType,
      setShopForBaseCommunity,
      setShopRequests,
    ]
  );

  const deleteCommunity = useCallback(
    (communityId: number) => {
      const filteredShopRequests = shopRequests.filter((r) => r.communityId !== communityId);
      setShopRequests(filteredShopRequests);
    },
    [shopRequests, setShopRequests]
  );

  const replaceCommunity = useCallback(
    (community: any, newCommunity: any) => {
      if (!community?.id || !newCommunity?.id) {
        return;
      }

      const shopRequestIndex = shopRequests.findIndex((r) => r.communityId === community.id);
      if (shopRequestIndex === -1) {
        return;
      }

      const updatedShopRequests = [...shopRequests];
      updatedShopRequests[shopRequestIndex] = {
        ...updatedShopRequests[shopRequestIndex],
        communityId: newCommunity.id,
        communityName: newCommunity.name,
      };
      setShopRequests(updatedShopRequests);
    },
    [shopRequests, setShopRequests]
  );

  return (
    <StyledModal
      open={open}
      onCancel={onCancel}
      onOk={handleConfirm}
      centered
      okButtonProps={{ disabled: isOkButtonDisabled }}
      title="Create Mystery Shop Request"
      okText="Create"
      width="min(90vw, 1240px)"
    >
      <ShopRequestModalContext.Provider
        value={{
          requestFrom,
          requestType,
          dueDate,
          repeat,
          name,
          shopRequests,
          specificDueDate,
          staffUsers,
          shopperId,
          auditorId,
          selectedCompany,
          shopForBaseCommunity,
          selectedBaseCommunity,
          baseCommunityIL,
          baseCommunityAL,
          baseCommunityMC,
          repeatEnabled,
          endRepeat,
          baseCommunityPropagateSelf,
          baseCommunityPropagateOthers,
          setBaseCommunityPropagateSelf,
          setBaseCommunityPropagateOthers,
          setEndRepeat,
          setRepeatEnabled,
          setBaseCommunityIL,
          setBaseCommunityAL,
          setBaseCommunityMC,
          setSelectedBaseCommunity,
          setShopForBaseCommunity,
          setSelectedCompany,
          setShopperId,
          setAuditorId,
          setName,
          setRequestFrom,
          setRequestType: handleRequestTypeChange,
          selectCommunity,
          setDueDate,
          setRepeat,
          setShopRequests,
          deleteCommunity,
          replaceCommunity,
          setSpecificDueDate,
        }}
      >
        <CreateShopRequestModalContent />
      </ShopRequestModalContext.Provider>
    </StyledModal>
  );
};

export default CreateShopRequestModal;
